import { useState, type ReactNode } from 'react';

export interface SecondarySidebarProps {
  children: ReactNode;
  /** Header title shown at the top of the sidebar. */
  title?: ReactNode;
  /** Slide the sidebar in on mobile (adds .sidebar-mobile-expanded). */
  mobileExpanded?: boolean;
  /** Mobile close (x) handler — typically the layout's `closeSecondarySidebar`. */
  onClose?: () => void;
  /** Start collapsed to the thin expand bar on desktop. */
  defaultCollapsed?: boolean;
  className?: string;
}

/**
 * Limitless secondary (light) sidebar — the second sidebar on the same side as the main
 * one, used for page-level sub-navigation, actions and filters. Collapses to a thin
 * expand-bar on desktop and slides in over the content on mobile.
 */
export function SecondarySidebar({
  children,
  title = 'Sidebar',
  mobileExpanded = false,
  onClose,
  defaultCollapsed = false,
  className = '',
}: SecondarySidebarProps) {
  const [collapsed, setCollapsed] = useState(defaultCollapsed);
  const toggleCollapse = () => setCollapsed((c) => !c);

  const classes = [
    'sidebar sidebar-secondary sidebar-expand-lg',
    collapsed ? 'sidebar-collapsed' : '',
    mobileExpanded ? 'sidebar-mobile-expanded' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={classes}>
      {/* Expand button — only visible (via CSS) while collapsed on desktop. */}
      <button
        type="button"
        className="btn btn-sidebar-expand sidebar-control h-100"
        onClick={toggleCollapse}
      >
        <i className="ph-caret-right" />
      </button>

      <div className="sidebar-content" style={{ overflowY: 'auto', maxHeight: '100%' }}>
        {/* Header */}
        <div className="sidebar-section sidebar-section-body d-flex align-items-center pb-0">
          <h5 className="mb-0">{title}</h5>
          <div className="ms-auto">
            <button
              type="button"
              className="btn btn-light border-transparent btn-icon rounded-pill btn-sm sidebar-control d-none d-lg-inline-flex"
              onClick={toggleCollapse}
            >
              <i className="ph-arrows-left-right" />
            </button>
            <button
              type="button"
              className="btn btn-light border-transparent btn-icon rounded-pill btn-sm d-lg-none"
              onClick={onClose}
            >
              <i className="ph-x" />
            </button>
          </div>
        </div>

        {children}
      </div>
    </div>
  );
}

export interface SecondarySidebarSectionProps {
  /** Section heading shown in the collapsible header band. */
  title: ReactNode;
  children: ReactNode;
  /** Start expanded. Default true. */
  defaultOpen?: boolean;
  /**
   * Drop the default body padding (.sidebar-section-body). Use this when the body is a
   * nav list (`ul.nav.nav-sidebar`), which brings its own spacing.
   */
  flush?: boolean;
}

/** A collapsible section within a {@link SecondarySidebar} (header + collapsible body). */
export function SecondarySidebarSection({
  title,
  children,
  defaultOpen = true,
  flush = false,
}: SecondarySidebarSectionProps) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="sidebar-section">
      <div className="sidebar-section-header border-bottom">
        <span className="fw-semibold">{title}</span>
        <div className="ms-auto">
          <a
            href="#"
            className={`text-reset ${open ? '' : 'collapsed'}`.trim()}
            onClick={(e) => {
              e.preventDefault();
              setOpen((o) => !o);
            }}
          >
            <i className="ph-caret-down collapsible-indicator" />
          </a>
        </div>
      </div>
      <div className={`collapse ${open ? 'show' : ''}`.trim()}>
        {flush ? children : <div className="sidebar-section-body">{children}</div>}
      </div>
    </div>
  );
}
